import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const variantStyles = {
  warning: {
    button:
      'text-yellow-700 hover:text-yellow-800 dark:text-yellow-300 dark:hover:text-yellow-200',
    content:
      'border-yellow-300 bg-yellow-100 text-yellow-900 dark:border-yellow-500 dark:bg-yellow-700 dark:text-yellow-100',
  },
  primary: {
    button: 'text-primary',
    content: 'border-primary bg-secondary text-foreground',
  },
} as const;

type HoverPopoverIconProps = {
  icon: LucideIcon;
  ariaLabel: string;
  message: ReactNode;
  variant?: keyof typeof variantStyles;
  size?: 'sm' | 'md';
  closeOnScroll?: boolean;
};

export function HoverPopoverIcon({
  icon: Icon,
  ariaLabel,
  message,
  variant = 'warning',
  size = 'sm',
  closeOnScroll = false,
}: HoverPopoverIconProps) {
  const [open, setOpen] = useState(false);
  const closeTimeoutRef = useRef<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const openPopover = () => {
    clearCloseTimeout();
    setOpen(true);
  };

  const closePopover = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = window.setTimeout(() => setOpen(false), 120);
  }, []);

  useEffect(() => {
    if (!open || !closeOnScroll) return;

    const handleScroll = () => {
      setOpen(false);
    };

    const trigger = triggerRef.current;
    if (!trigger) return;

    let scrollableParent: HTMLElement | null = trigger.parentElement;
    while (scrollableParent) {
      const { overflow, overflowY } = window.getComputedStyle(scrollableParent);
      if (
        overflow === 'auto' ||
        overflow === 'scroll' ||
        overflowY === 'auto' ||
        overflowY === 'scroll'
      ) {
        break;
      }
      scrollableParent = scrollableParent.parentElement;
    }

    const scrollTarget = scrollableParent || window;
    scrollTarget.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollTarget.removeEventListener('scroll', handleScroll);
    };
  }, [open, closeOnScroll]);

  const styles = variantStyles[variant];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-full transition-colors hover:bg-muted',
            size === 'sm' ? 'size-6' : 'size-7',
            styles.button
          )}
          aria-label={ariaLabel}
          onMouseEnter={openPopover}
          onMouseLeave={closePopover}
        >
          <Icon className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn('w-80 shadow-none ring-0', styles.content)}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onMouseEnter={openPopover}
        onMouseLeave={closePopover}
      >
        {typeof message === 'string' ? (
          <p className="text-xs leading-relaxed">{message}</p>
        ) : (
          <div className="text-xs leading-relaxed">{message}</div>
        )}
      </PopoverContent>
    </Popover>
  );
}
